import { useEffect, useCallback, useState } from "react";
import { useGetNotificationSettingQuery } from "../../features/NewPrices2024/api/globalSettingsAPI";
import usePopup from "./usePopup";
import GlobalNotificationPopup from "../../features/popups/GlobalNotificationPopup";
import { useAuth } from "./useAuth";
import dayjs from "dayjs";
import { AccessLevelE, ClientTypeE, NotificationTypeE } from "../../models/enums";
import type { GlobalNotificationT } from "../../features/NewPrices2024/models/models";
import { useAppSelector } from "../../store/hooks";
import { selectCurrentLang } from "../../store/selectors/appSelectors";
import { selectIsTourGateCleared } from "../../store/selectors/productTourSelectors";
import { isLanguageMatched } from "../../features/NewPrices2024/utils/notificationHelpers";

// Global ref to track processed popups across all hook instances
// This prevents duplicate popups when hook is called multiple times
const globalProcessedPopupsRef = new Set<string>();
const globalActiveCampaignNotificationRef = { current: null as GlobalNotificationT | null };

/**
 * Shows a global notification popup for users based on global notification settings.
 * Returns active Campaign notification for custom banner display.
 *
 * Popup notifications wait for the product tour: on first login the welcome message and the tour
 * steps render above every popup, so one opened at the same moment is buried under them and is
 * marked as read on mount without the user ever reading it. See the tour gate below.
 */
const useGlobalNotification = () => {
  const { currentUser } = useAuth();
  const currentLang = useAppSelector(selectCurrentLang);
  const isTourGateCleared = useAppSelector(selectIsTourGateCleared);
  const { data: notifications } = useGetNotificationSettingQuery(undefined, {
    skip: !currentUser?.id,
  });

  const { createPopup } = usePopup();
  const [activeCampaignNotification, setActiveCampaignNotification] = useState<GlobalNotificationT | null>(
    globalActiveCampaignNotificationRef.current
  );

  // Show the notification popup if it is allowed
  const showNotification = useCallback((notification: GlobalNotificationT) => {
    if (notification?.TitleConst && currentUser?.id && notification?.IsActive) {
      // Create unique key for this notification
      const notificationKey = `${notification.TitleConst}-${currentUser?.id}`;
      
      // Skip if this notification was already processed (using global ref)
      if (globalProcessedPopupsRef.has(notificationKey)) {
        return;
      }

      // Check notification type: Popup (0) or Campaign (1)
      // Backend always sends Type: 0 = Popup, 1 = Campaign
      const notificationType = notification.Type;
      const storageKey = `notification-${notificationKey}`;

      // Show the popup only if the notification is for the current user type and language
      // Languages is required field, so it will always be present
      if (
        (notification.ClientTypes.includes(currentUser.clientType) ||
          // Condition for private delivery service since we can detect it only by access level
          (notification.ClientTypes.includes(
            ClientTypeE["Radio station"]
          ) &&
            currentUser.accessLevel === AccessLevelE.DeliveryServicePrivate)) &&
        notification.Languages &&
        isLanguageMatched(currentLang, notification.Languages)
      ) {
        const startDate = dayjs(notification.FromDate);
        const endDate = dayjs(notification.ToDate);
        const currentDate = dayjs();

        // Show the popup only if the notification is in the date range
        if (currentDate.isAfter(startDate) && currentDate.isBefore(endDate)) {
          if (notificationType === NotificationTypeE.Campaign) {
            // Campaign notifications are shown always (not checked against localStorage)
            // They should be visible until user manually closes them
            globalActiveCampaignNotificationRef.current = notification;
            setActiveCampaignNotification(notification);
            globalProcessedPopupsRef.add(notificationKey);
          } else {
            // The welcome message / tour holds the screen on first login. Leave the notification
            // untouched — not marked as processed — so the effect below re-runs and opens it once
            // the user closes the welcome message, closes the tour, or finishes its last step.
            if (!isTourGateCleared) {
              return;
            }

            // Popup type notifications: show only if not read yet
            if (!localStorage.getItem(storageKey)) {
              createPopup({
                body: <GlobalNotificationPopup notification={notification} />,
                className: "globalNotificationPopup",
              });
              globalProcessedPopupsRef.add(notificationKey);
            }
          }
        }
      }
    }
  }, [createPopup, currentUser?.id, currentUser?.clientType, currentUser?.accessLevel, currentLang, isTourGateCleared]);

  useEffect(() => {
    // Sync state with global ref for all hook instances
    if (globalActiveCampaignNotificationRef.current) {
      setActiveCampaignNotification(globalActiveCampaignNotificationRef.current);
    }
    
    // Process notifications - global refs prevent duplicate processing
    if (notifications?.NotificationSettingModels?.length) {
      notifications.NotificationSettingModels.forEach((notification) =>
        showNotification(notification)
      );
    }
  }, [notifications?.NotificationSettingModels, showNotification]);

  return { activeCampaignNotification, setActiveCampaignNotification };
};

export default useGlobalNotification;
